import { makeAutoObservable } from 'mobx'
import { numberFormat } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing/api/billing'
import { PriceInfoStore } from 'entities/Billing/ui/PriceInfo/store/PriceInfoStore'
import { type PriceInfoViewModel } from 'entities/Billing/model/PriceInfoViewModel'
import { smartRound } from 'entities/Billing/lib/smartRound'

interface ICombinedPriceInfoStoreConfig {
  entities: IEntitiesStore
}

export class CombinedPriceInfoStore implements PriceInfoViewModel {
  readonly seatStore: PriceInfoStore
  readonly numbersStore: PriceInfoStore
  private _entities: IEntitiesStore
  private _seatsRequired = 0
  private _licensesRequired = 0

  constructor(config: ICombinedPriceInfoStoreConfig) {
    this._entities = config.entities

    this.seatStore = new PriceInfoStore((count: number, cancelToken) =>
      BillingApi.getBillingSeatsPreview(
        {
          plan: this._entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          seats: count,
          use_unassigned_seats: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )

    this.numbersStore = new PriceInfoStore((count: number, cancelToken) =>
      BillingApi.getBillingPreview(
        {
          plan: this._entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          numbers: count,
          use_unassigned_numbers: 0,
          use_bought_numbers: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )

    makeAutoObservable(this)
  }

  get seatsAvailable() {
    return this._entities.subscriptionStore.seatsAvailable
  }

  get licensesAvailable() {
    return this._entities.subscriptionStore.licensesCount
  }

  get seatsRequired() {
    return this._seatsRequired
  }

  get licensesRequired() {
    return this._licensesRequired
  }

  get seatsToCharge() {
    return Math.max(0, this._seatsRequired - this.seatsAvailable)
  }

  get licensesToCharge() {
    return Math.max(0, this._licensesRequired - this.licensesAvailable)
  }

  get priceInfoUiData() {
    const seatData = this.seatStore?.priceInfo
    const numbersData = this.numbersStore?.priceInfo
    const seatUiData = this.seatStore?.priceInfoUiData
    const numbersUiData = this.numbersStore?.priceInfoUiData

    const hasSeatData = seatData && seatUiData
    const hasNumbersData = numbersData && numbersUiData

    if (!hasSeatData && !hasNumbersData) return null

    const combinedSubtotal = (seatData?.subtotal ?? 0) + (numbersData?.subtotal ?? 0)
    const combinedUnusedTime = ((seatData?.unused_time ?? 0) + (numbersData?.unused_time ?? 0)) * -1
    const combinedTaxes = (seatData?.taxes_amount ?? 0) + (numbersData?.taxes_amount ?? 0)
    const combinedTotalDueToday =
      (seatData?.total_due_today ?? 0) + (numbersData?.total_due_today ?? 0)

    const taxRate = seatData?.tax_rates?.length
      ? seatData.tax_rates[0]
      : numbersData?.tax_rates?.length
      ? numbersData.tax_rates[0]
      : null

    const seatTierDetails = seatUiData?.tierDetails?.map((details) => ({
      tier: details.tier,
      count: details.count,
      perPrice: details.perPrice,
      type: 'seat' as const,
    }))

    const numbersTierDetails = numbersUiData?.tierDetails?.map((details) => ({
      tier: details.tier,
      count: details.count,
      perPrice: details.perPrice,
      type: 'number' as const,
    }))

    const interval = seatUiData?.interval ?? numbersUiData?.interval
    const nextChargeDate = seatUiData?.nextChargeDate ?? numbersUiData?.nextChargeDate
    const noConvertedNextChargeDate =
      seatUiData?.noConvertedNextChargeDate ?? numbersUiData?.noConvertedNextChargeDate
    const nextCharge = seatUiData?.nextCharge ?? numbersUiData?.nextCharge

    return {
      tierDetails: [...(seatTierDetails || []), ...(numbersTierDetails || [])],
      interval,
      subtotal: numberFormat({
        value: combinedSubtotal / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      unusedTime: numberFormat({
        value: combinedUnusedTime / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      taxes: taxRate
        ? {
            total: numberFormat({
              value: combinedTaxes / 100,
              currency: 'USD',
              minimumFractionDigits: 2,
            }),
            percent: smartRound(taxRate.percent, 1),
            jurisdiction: taxRate.jurisdiction,
          }
        : null,
      totalDueToday: numberFormat({
        value: combinedTotalDueToday / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      nextChargeDate,
      noConvertedNextChargeDate,
      nextCharge,
    }
  }

  get isLoading() {
    return this.seatStore.requestInfoStatus.loading || this.numbersStore.requestInfoStatus.loading
  }

  get requestInfoStatus() {
    return {
      status: {
        isLoading: this.isLoading,
      },
    }
  }

  get seatPriceInfoStore() {
    return this.seatStore
  }

  get numbersPriceInfoStore() {
    return this.numbersStore
  }

  updatePriceData = async (licensesRequired: number, seatsRequired = 0) => {
    this._seatsRequired = seatsRequired
    this._licensesRequired = licensesRequired

    const promises: Promise<void>[] = []

    if (this.seatsToCharge > 0) {
      promises.push(this.seatStore.loadPriceData(this.seatsToCharge))
    } else {
      this.seatStore.reset()
    }

    if (this.licensesToCharge > 0) {
      promises.push(this.numbersStore.loadPriceData(this.licensesToCharge))
    } else {
      this.numbersStore.reset()
    }

    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }

  reset = () => {
    this._seatsRequired = 0
    this._licensesRequired = 0
    this.seatStore.reset()
    this.numbersStore.reset()
  }
}
